import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { UserPlus, Search, RefreshCw, X, FileText, FolderOpen, Building2, Contact, Users as UsersIcon, Trash2, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const emptyCustomer = {
  company_id: '', customer_type: 'privato',
  first_name: '', last_name: '', company_name: '',
  fiscal_code: '', vat_number: '', email: '', phone: '',
  birth_place: '', birth_date: '', address: '', city: '', province: '', cap: '', iban: '', notes: '', tags: [],
};

const displayName = (c) => c.customer_type === 'azienda' && c.company_name
  ? c.company_name
  : `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—';

export default function Customers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [tagFilter, setTagFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all' | 'me' | 'unassigned' | <user_id>
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (debounced) params.q = debounced;
      if (isAdmin && companyFilter !== 'all') params.company_id = companyFilter;
      if (assigneeFilter && assigneeFilter !== 'all') params.assigned_to = assigneeFilter;
      const res = await api.listCustomers(params);
      setItems(res.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile caricare le anagrafiche');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, companyFilter, assigneeFilter]);

  useEffect(() => {
    (async () => {
      if (isAdmin) {
        try { const c = await api.listCompanies({}); setCompanies(c || []); } catch { /* ignore */ }
      } else if (user?.company_id) {
        setForm(f => ({ ...f, company_id: user.company_id }));
      }
      try {
        const users = await api.listUsers();
        const filtered = isAdmin ? users : users.filter(u => u.company_id === user?.company_id);
        setAssignableUsers(filtered);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allTags = useMemo(() => {
    const s = new Set();
    items.forEach(i => (i.tags || []).forEach(t => s.add(t)));
    return [...s].sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (tagFilter === 'all') return items;
    return items.filter(i => (i.tags || []).includes(tagFilter));
  }, [items, tagFilter]);

  const openNew = () => {
    setForm({ ...emptyCustomer, company_id: isAdmin ? '' : (user?.company_id || '') });
    setDlgOpen(true);
  };

  const handleSave = async () => {
    if (!form.company_id) { toast.error('Seleziona l’azienda partner'); return; }
    if (form.customer_type === 'privato' && !form.first_name && !form.last_name) {
      toast.error('Inserisci almeno Nome o Cognome'); return;
    }
    if (form.customer_type === 'azienda' && !form.company_name) {
      toast.error('Inserisci la ragione sociale'); return;
    }
    setSaving(true);
    try {
      await api.createCustomer(form);
      toast.success('Anagrafica creata');
      setDlgOpen(false); setForm(emptyCustomer); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione anagrafica');
    } finally { setSaving(false); }
  };

  const handleMigrate = async () => {
    if (!isAdmin) return;
    setMigrating(true);
    try {
      const res = await api.migrateCustomers();
      toast.success(res?.customers_created_or_linked > 0 ? `${res.customers_created_or_linked} anagrafiche migrate/collegate` : 'Nessuna anagrafica da migrare');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore migrazione');
    } finally { setMigrating(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Contact className="size-6 text-primary" /> Anagrafiche Clienti
          </h1>
          <p className="text-sm text-muted-foreground">
            Cerca, aggiorna e organizza tutti i clienti. Ogni contratto firmato viene automaticamente collegato all’anagrafica corrispondente per il cross-sell e l’up-sell.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={handleMigrate} disabled={migrating} data-testid="customers-migrate-btn">
              <RefreshCw className={`size-4 mr-1 ${migrating ? 'animate-spin' : ''}`} /> Migra da contratti
            </Button>
          )}
          <Button size="sm" onClick={openNew} data-testid="customers-new-btn">
            <UserPlus className="size-4 mr-1" /> Nuova anagrafica
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Elenco</CardTitle>
          <CardDescription>{filtered.length} di {items.length} anagrafiche visibili.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input placeholder="Cerca nome, CF, P.IVA, email, città..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" data-testid="customers-search" />
              {search && (
                <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Pulisci"><X className="size-4" /></button>
              )}
            </div>
            {isAdmin && (
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger className="w-full md:w-[220px] h-9" data-testid="customers-company-filter"><SelectValue placeholder="Azienda" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le aziende</SelectItem>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {allTags.length > 0 && (
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="w-full md:w-[180px] h-9" data-testid="customers-tag-filter"><SelectValue placeholder="Tag" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tag</SelectItem>
                  {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger className="w-full md:w-[220px] h-9" data-testid="customers-assignee-filter"><SelectValue placeholder="Assegnatario" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Tutti gli assegnatari</SelectItem>
                <SelectItem value="me">📌 Solo i miei (assegnati a me)</SelectItem>
                <SelectItem value="unassigned">— Non assegnate</SelectItem>
                {assignableUsers.length > 0 && <div className="border-t my-1" />}
                {assignableUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name || u.email} · <span className="text-muted-foreground">{u.role}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome / Ragione sociale</TableHead>
                  <TableHead>Contatti</TableHead>
                  <TableHead>CF / P.IVA</TableHead>
                  <TableHead>Città</TableHead>
                  <TableHead>Assegnato a</TableHead>
                  <TableHead className="text-right">Contratti</TableHead>
                  <TableHead className="text-right">Documenti</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Caricamento…</TableCell></TableRow>}
                {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Nessuna anagrafica trovata</TableCell></TableRow>}
                {!loading && filtered.map(c => (
                  <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {c.customer_type === 'azienda' ? <Building2 className="size-4 text-muted-foreground" /> : <UsersIcon className="size-4 text-muted-foreground" />}
                        <Link to={`/customers/${c.id}`} className="font-medium hover:underline text-primary">{displayName(c)}</Link>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {c.email && <div className="truncate max-w-[200px]" title={c.email}>{c.email}</div>}
                      {c.phone && <div className="text-muted-foreground">{c.phone}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {c.fiscal_code || <span className="text-muted-foreground">—</span>}
                      {c.vat_number && <div className="text-muted-foreground">{c.vat_number}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{c.city}{c.province ? ` (${c.province})` : ''}</TableCell>
                    <TableCell className="text-xs">
                      {c.assigned_to_name ? (
                        <div>
                          <div className="font-medium">{c.assigned_to_name}</div>
                          {c.assigned_to_role && <div className="text-muted-foreground">{c.assigned_to_role}</div>}
                        </div>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{c.contracts_count || 0}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.documents_count || 0}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).map(t => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" asChild data-testid={`customer-open-${c.id}`}>
                        <Link to={`/customers/${c.id}`}><FolderOpen className="size-4" /></Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog nuova anagrafica */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nuova anagrafica cliente</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {isAdmin && (
              <div className="md:col-span-2">
                <Label>Azienda partner</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm(f => ({ ...f, company_id: v }))}>
                  <SelectTrigger data-testid="customer-form-company"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                  <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Tipo</Label>
              <Select value={form.customer_type} onValueChange={(v) => setForm(f => ({ ...f, customer_type: v }))}>
                <SelectTrigger data-testid="customer-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="privato">Privato</SelectItem>
                  <SelectItem value="azienda">Azienda</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.customer_type === 'azienda' ? (
              <>
                <div className="md:col-span-1"><Label>Ragione sociale</Label><Input value={form.company_name} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))} data-testid="customer-form-company-name" /></div>
                <div><Label>P.IVA</Label><Input value={form.vat_number} onChange={(e) => setForm(f => ({ ...f, vat_number: e.target.value.toUpperCase() }))} /></div>
                <div><Label>Codice fiscale</Label><Input value={form.fiscal_code} onChange={(e) => setForm(f => ({ ...f, fiscal_code: e.target.value.toUpperCase() }))} /></div>
              </>
            ) : (
              <>
                <div><Label>Nome</Label><Input value={form.first_name} onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))} data-testid="customer-form-firstname" /></div>
                <div><Label>Cognome</Label><Input value={form.last_name} onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))} /></div>
                <div className="md:col-span-2"><Label>Codice fiscale</Label><Input value={form.fiscal_code} onChange={(e) => setForm(f => ({ ...f, fiscal_code: e.target.value.toUpperCase() }))} /></div>
              </>
            )}
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
            <div className="md:col-span-2"><Label>Indirizzo</Label><Input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} /></div>
            <div><Label>Città</Label><Input value={form.city} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Prov.</Label><Input value={form.province} maxLength={2} onChange={(e) => setForm(f => ({ ...f, province: e.target.value.toUpperCase() }))} /></div>
              <div><Label>CAP</Label><Input value={form.cap} onChange={(e) => setForm(f => ({ ...f, cap: e.target.value }))} /></div>
            </div>
            <div className="md:col-span-2"><Label>Note</Label><Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="customer-form-save">
              {saving ? 'Salvo…' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
